/**
 * Build a deep link URL to the Analysis page for a specific parameter,
 * pre-selecting all available sources and centering the date range
 * around a given ICP test date.
 */
export interface TrendLinkParams {
  /** Analysis parameter key (e.g., 'alkalinity', 'no3') */
  paramKey: string;
  /** Available sources for this parameter (e.g., ['icp', 'manual', 'mastertronic']) */
  sources: string[];
  /** ICP test date as ISO string */
  testDate: string;
  /** Days before and after test date to show (default: 30) */
  rangeDays?: number;
}

/**
 * Builds a URL path + query string for the Analysis page.
 * Returns null if no sources are available.
 */
export function buildAnalysisTrendUrl(params: TrendLinkParams): string | null {
  const { paramKey, sources, testDate, rangeDays = 30 } = params;

  if (sources.length === 0) {
    return null;
  }

  const center = new Date(testDate);
  const start = new Date(center);
  start.setDate(start.getDate() - rangeDays);
  const end = new Date(center);
  end.setDate(end.getDate() + rangeDays);

  const formatDate = (d: Date): string => d.toISOString().split('T')[0] ?? '';

  const searchParams = new URLSearchParams();
  searchParams.set('start_date', formatDate(start));
  searchParams.set('end_date', formatDate(end));

  // Add each source as a separate params entry
  for (const source of sources) {
    searchParams.append('params', `${paramKey}:${source}`);
  }

  return `/analysis?${searchParams.toString()}`;
}
